import { useMemo } from 'react';
import type { InventoryAuthority } from '@qkindex/contracts';
import type { NavigationCount } from '../../components/ui/navigation';
import type { Item } from '../../domain/item';
import type { Location } from '../../domain/location';
import type { CardSize } from '../../hooks/useUiPreferences';
import type { CollectionView, ItemSort } from '../collection/types';
import type { BulkLocationResult } from './locationOperations';
import type { LocationFilterScope } from './useItemFilters';
import { useCollectionTags } from '../sharing/useCollectionTags';
import { InventoryItemsPage } from './InventoryItemsPage';

export interface ItemsPageProps {
  authority: InventoryAuthority;
  items: Item[];
  locations: Location[];
  loading: boolean;
  error: string | null;
  phoneLayout: boolean;
  onCountChange?: (count: NavigationCount) => void;
  hideCount?: boolean;
  stickyTop?: number;
  shellHeader?: React.ReactNode;
  view: CollectionView;
  onViewChange: (view: CollectionView) => void;
  sort: ItemSort;
  onSortChange: (sort: ItemSort) => void;
  cardSize: CardSize;
  removeMargins?: boolean;
  readOnly?: boolean;
  canDelete?: boolean;
  disableBulkActions?: boolean;
  onRefresh?: () => void;
  incompleteResults?: boolean;
  inventoryStatus?: 'loading' | 'updating';
  additionsBlocked?: boolean;
  capacityNotice?: string;
  onOpenItem: (item: Item) => void;
  onAddItem: () => void;
  onMovedToBin?: (items: Item[]) => void;
  updateLastUsedLocation: (location: string) => Promise<void>;
  onOpenBin?: () => void;
  onMoveSelection?: (
    items: Item[],
    location: Location,
  ) => Promise<BulkLocationResult>;
  locationMoveError?: (
    items: Item[],
    location?: Location,
  ) => string | undefined;
  moveLocations?: Location[];
  onLoadMoveLocations?: () => void;
  moveLocationsLoading?: boolean;
  locationFilterScope?: LocationFilterScope;
  tagCollections?: { collectionId: string; name: string }[];
}

export function ItemsPage(props: ItemsPageProps) {
  const { authority, items, locations, tagCollections, locationFilterScope } =
    props;
  const collectionIds = useMemo(
    () =>
      tagCollections?.map((collection) => collection.collectionId) ??
      locationFilterScope?.collectionIds ?? [
        ...new Set(
          [
            authority.collectionId,
            ...items.map((item) => item.collectionId || ''),
            ...locations.map((location) => location.collectionId || ''),
          ].filter(Boolean),
        ),
      ],
    [
      tagCollections,
      locationFilterScope?.collectionIds,
      authority.collectionId,
      items,
      locations,
    ],
  );
  const tagState = useCollectionTags(authority.userId, collectionIds);
  return (
    <InventoryItemsPage
      {...props}
      identity={{
        ...authority,
        scopeKey: `${authority.collectionId}:${authority.userId}`,
      }}
      tagState={tagState}
    />
  );
}
